import {
  GroupedJourneyGroupType,
  GroupedJourneys,
} from "../../types/analytics/FilterAvailableValues";

/**
 * Minimal shape of the i18next `t` function these helpers need, so they stay pure
 * and can be called from components using either `useTranslation` or the i18next
 * `t` import.
 */
export type JourneyFilterTranslate = (
  key: string,
  defaultValue?: string,
) => string;

/**
 * ClientSent journey titles are admin-entered content and must be shown as-is.
 * Every other group stores a TranslationKeyIdentifier, so it needs translating
 * before display - otherwise the raw key leaks into the UI.
 *
 * Do not translate unconditionally: i18next echoes an unknown key back, which
 * hides the problem right up until an admin names a journey after a real key.
 */
export function journeyFilterTitle(
  groupType: GroupedJourneyGroupType,
  title: string,
  t: JourneyFilterTranslate,
): string {
  return groupType === "CLIENT-SENT" ? title : t(title, title);
}

/** Display titles for the given journey keys, in the order the groups supply them. */
export function journeyFilterTitlesForKeys(
  groups: GroupedJourneys[],
  keys: string[],
  t: JourneyFilterTranslate,
): string[] {
  return groups.flatMap((group) =>
    group.journeys
      .filter((journey) => keys.includes(journey.key))
      .map((journey) => journeyFilterTitle(group.groupType, journey.title, t)),
  );
}

/**
 * Display text for a journey label read back out of a saved filter document.
 *
 * Saved documents deliberately store the untranslated title, so a shared save is
 * not frozen into whichever language its author used. The group type is not
 * recoverable here - these journeys are the ones missing from the current page -
 * so fall back to i18next echoing the value for admin-entered names.
 */
export function savedJourneyLabelForDisplay(
  label: string,
  t: JourneyFilterTranslate,
): string {
  return t(label, label);
}

/**
 * Titles keyed by journey guid, for data that references journeys individually
 * rather than by the grouped filter key.
 */
export function journeyFilterTitlesByGuid(
  groups: GroupedJourneys[],
  t: JourneyFilterTranslate,
): { key: string; value: string }[] {
  return groups.flatMap((group) =>
    group.journeys.flatMap((journey) => {
      const title = journeyFilterTitle(group.groupType, journey.title, t);
      return journey.guids.map((guid) => ({ key: guid, value: title }));
    }),
  );
}
